/** Google Maps JS style arrays for the regional store map. */

export type MapTheme = "night" | "standard";

export type MapStyleRule = {
  featureType?: string;
  elementType?: string;
  stylers: Record<string, string | number | boolean>[];
};

export const MAP_THEME_STORAGE_KEY = "regionalStoreMapTheme";

/** Dark teal night map — approximate low-contrast style; keep admin + major roads. */
export const NIGHT_MAP_STYLES: MapStyleRule[] = [
  // Land base: cyan-green charcoal (fig mark 3)
  { elementType: "geometry", stylers: [{ color: "#1c2f2b" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#b4bbb8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1c2f2b" }] },

  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#2a3c38" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8f9894" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#c8cfcc" }],
  },

  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#1c2f2b" }],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry",
    stylers: [{ color: "#203430" }],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#1a2c27" }],
  },
  {
    featureType: "landscape.natural.terrain",
    elementType: "geometry",
    stylers: [{ color: "#172722" }],
  },

  // Parks: slightly greener cyan-green patches
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "#274038" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },

  // Roads: muted indigo (fig mark 2), still clearer than land
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#3a465c" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#161e28" }],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#4a5870" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [{ color: "#a8b0ac" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#5a6a84" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1a222e" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#c2c9c5" }],
  },
  {
    featureType: "road.local",
    elementType: "geometry",
    stylers: [{ color: "#343f52" }],
  },
  {
    featureType: "road.local",
    elementType: "labels",
    stylers: [{ visibility: "simplified" }],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7a8480" }],
  },

  { featureType: "transit", stylers: [{ visibility: "off" }] },

  // Water: slightly brighter, more blue (fig mark 1)
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#1f5364" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#5a7378" }],
  },
];

export const STANDARD_MAP_STYLES: MapStyleRule[] = [];

export function stylesForMapTheme(theme: MapTheme): MapStyleRule[] {
  return theme === "night" ? NIGHT_MAP_STYLES : STANDARD_MAP_STYLES;
}

export function readStoredMapTheme(): MapTheme {
  if (typeof localStorage === "undefined") return "night";
  try {
    const raw = localStorage.getItem(MAP_THEME_STORAGE_KEY);
    if (raw === "standard" || raw === "night") return raw;
  } catch {
    // private mode / blocked storage
  }
  return "night";
}

export function writeStoredMapTheme(theme: MapTheme) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(MAP_THEME_STORAGE_KEY, theme);
  } catch {
    // quota / private mode
  }
}
